/// <amd-module name="Comments/ReduxOnDemand/LightCommentItem" />
import { ICommentReaction, CommentReactionType, IComment } from "Comments/Common/Contracts";
import { UsageTelemetryHelper } from "Comments/Common/Telemetry";
import { CommentItem } from "Comments/Item/CommentItem";
import { ICommentReactionTypeInfo, IReactionTypeToInfoMap } from "Comments/Reactions/Contracts";
import { IConnectedCommentItemProps } from "Comments/Redux/ConnectedCommentItem.Props";
import { ILightCommentItemProps } from "Comments/ReduxOnDemand/LightCommentItem.Props";
import * as React from "react";
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { shallowCompare } from "VSS/Core/Util/Object";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { noop } from "VSSUI/Util";

const ReactionTypesCount: number = 6;

export interface ILightCommentItemState {
    useReduxComponent: boolean;
    startEditOnMount: boolean;
    deleteOnMount: boolean;
}

/**
 * Light comment item which will switch to the redux version on-demand.
 */
export class LightCommentItem extends VssComponent<ILightCommentItemProps, ILightCommentItemState> {
    private _contributionService: IVssContributionService;
    private _usageTelemetry: UsageTelemetryHelper;
    private _tempReactionTypeToInfoMap: IReactionTypeToInfoMap;

    // pendingAction can be -1, 0 or +1. It starts with 0 i.e. there is no pending action irrespective
    // of whether the user is already engaged or not. If the user is already engaged and if he clicks
    // on the reaction, then the pending action would be set to -1 and if he clicks on it again, the
    // pending action would be 0, so we avoid making a call to the server in this case
    private _pendingReactionActions: IObservableArray<number>;

    constructor(props: ILightCommentItemProps, context: IVssComponentContext) {
        super(props, context);

        this.state = { useReduxComponent: false, startEditOnMount: false, deleteOnMount: false };

        this._usageTelemetry = new UsageTelemetryHelper(props.artifactInfo, context.pageContext);

        this._tempReactionTypeToInfoMap = this._getCommentReactions(props.comment);
        this._pendingReactionActions = new ObservableArray<number>(new Array<number>(ReactionTypesCount).fill(0));
    }

    public render(): JSX.Element {
        const { comment, onRenderLoadingCommentComponent = this._getLightCommentItem } = this.props;
        const { deleteOnMount, startEditOnMount } = this.state;

        if (this.state.useReduxComponent) {
            return (
                <WrappedComponent<IConnectedCommentItemProps>
                    wrappedType="dynamic-comment-item"
                    wrappedProps={
                        {
                            ...this.props,
                            commentId: comment.id,
                            deleteOnMount,
                            startEditOnMount,
                            autoLoadComment: true,
                            onRenderLoadingCommentComponent,
                            pendingReactionActions: this._pendingReactionActions
                        } as IConnectedCommentItemProps
                    }
                />
            );
        }

        return this._getLightCommentItem();
    }

    public shouldComponentUpdate(newProps: ILightCommentItemProps, newState: ILightCommentItemState): boolean {
        return !shallowCompare(newProps, this.props) || !shallowCompare(newState, this.state);
    }

    private _getLightCommentItem = (): JSX.Element => {
        const {
            artifactInfo,
            readonly = false,
            canEdit,
            comment,
            className,
            bodyClassName,
            viewerClassName,
            viewerContentClassName,
            compactMode,
            onRenderPersona,
            onRenderDeleted,
            getPermalink,
            isReactionsEnabled
        } = this.props;

        if (comment.isDeleted) {
            return onRenderDeleted ? onRenderDeleted(comment) : <></>;
        }

        return (
            <CommentItem
                artifactInfo={artifactInfo}
                canEdit={canEdit}
                readonly={readonly}
                comment={comment}
                className={css("light-comment-item", className)}
                bodyClassName={bodyClassName}
                viewerClassName={viewerClassName}
                contentClassName={viewerContentClassName}
                compactMode={compactMode}
                getPermalink={getPermalink}
                onRenderPersona={onRenderPersona}
                onStartEdit={this._onStartEdit}
                onConfirmDelete={this._onConfirmDelete}
                isReactionsEnabled={isReactionsEnabled}
                reactionTypeToInfoMap={this._tempReactionTypeToInfoMap}
                onAddReaction={this._onAddReaction}
                onDeleteReaction={this._onDeleteReaction}
                onGetRecentlyReactedUsers={this._onGetRecentlyReactedUsers}
                // no edit in the light version, the connected-comment-item component will used for that:
                isEditing={false}
                onRenderEditor={noop as any}
                onCancelEdit={noop}
                onUpdate={noop}
            />
        );
    };

    private _onStartEdit = (commentId: number): void => {
        this._fetchReduxComponent(() => this.setState({ useReduxComponent: true, startEditOnMount: true }), true);
    };

    private _onConfirmDelete = (commentId: number, confirmed: boolean): void => {
        if (confirmed) {
            this._fetchReduxComponent(() => this.setState({ useReduxComponent: true, deleteOnMount: confirmed }));
        }
    };

    private _onAddReaction = (commentId: number, reactionType: CommentReactionType): void => {
        this._onUpdateReaction(reactionType, true);
    };

    private _onDeleteReaction = (commentId: number, reactionType: CommentReactionType): void => {
        this._onUpdateReaction(reactionType, false);
    };

    private _onUpdateReaction(reactionType: CommentReactionType, isEngaged: boolean = true): void {
        const reactionTypeInfo = this._tempReactionTypeToInfoMap[reactionType];
        if (reactionTypeInfo.isCurrentUserEngaged === isEngaged) {
            return;
        }

        const delta = isEngaged ? 1 : -1;
        reactionTypeInfo.totalCount += delta;
        reactionTypeInfo.isCurrentUserEngaged = isEngaged;
        this._tempReactionTypeToInfoMap = { ...this._tempReactionTypeToInfoMap };

        const reactionActionsTracker = this._pendingReactionActions;
        const pendingAction = reactionActionsTracker.value[reactionType] + delta;
        reactionActionsTracker.change(reactionType, pendingAction === 0 ? 0 : pendingAction < 0 ? -1 : 1);

        // Can't make the _tempReactionTypeToInfoMap the component's state variable as the Reaction components
        // rely on 'isCurrentUserEngaged' value to call add or delete reaction correctly and we might end up
        // with user's consecutive (very fast) clicks on the same reaction type racing with 'isCurrentUserEngaged'
        // value being updated
        if (this.state.useReduxComponent) {
            this.forceUpdate();
        } else {
            this._fetchReduxComponent(() => this.setState({ useReduxComponent: true }));
        }
    }

    private _onGetRecentlyReactedUsers = (commentId: number, reactionType: CommentReactionType): void => {
        // No need to track this call as pending action here as we'll handle the actual call
        // to server to get users in connected comment component
        this._fetchReduxComponent(() => this.setState({ useReduxComponent: true }));
    };

    // fetch the redux ourselves instead of using wrapped component, to have better control when the DOM is updated
    private _fetchReduxComponent(callback: () => void, includeEditor: boolean = false): void {
        const ids = ["ms.vss-comments.redux"];
        if (includeEditor) {
            ids.push("ms.vss-comments.editor");
        }

        this.trackPromise(
            this._getContributionService()
                .getContributionsAsync(ids)
                .then(callback)
                .catch(e => e && !e.canceled && this._usageTelemetry.traceError("_fetchReduxComponent", e, { ids, includeEditor }))
        );
    }

    private _getContributionService(): IVssContributionService {
        if (!this._contributionService) {
            this._contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        }

        return this._contributionService;
    }

    private _getCommentReactions(comment: IComment): IReactionTypeToInfoMap {
        const result: IReactionTypeToInfoMap = {};
        for (let i = 0; i < ReactionTypesCount; ++i) {
            result[i] = { totalCount: 0, isCurrentUserEngaged: false, engagedUsers: [] };
        }

        const commentReactions: ICommentReaction[] = comment.reactions || [];
        commentReactions.forEach((reaction: ICommentReaction) => {
            if (reaction) {
                const reactionTypeInfo: ICommentReactionTypeInfo = {
                    totalCount: reaction.count,
                    isCurrentUserEngaged: reaction.isCurrentUserEngaged,
                    engagedUsers: [] // This will be updated later on when user hovers over this reaction
                };

                result[reaction.type] = reactionTypeInfo;
            }
        });

        return result;
    }
}

VssComponent.register("light-comment-item", LightCommentItem);
